/**
 * Small rules for the Ask Canira page, kept out of the component so they can
 * be tested without rendering it.
 */

/** Admins, plus the beta list (the server says so as `askCanira` on /auth/me). */
export function canUseAskCanira(user) {
  if (!user) return false;
  return user.role === 'admin' || user.role === 'super_admin' || user.askCanira === true;
}

export const READ_ALOUD_KEY = 'canira_ask_read_aloud';
export const POLL_MS = 1500;
// While a clip a reply made is still being made, the page asks again every
// CLIP_REFRESH_MS (for up to CLIP_REFRESH_MAX_MS) so its card turns Ready
// without the host reopening the conversation.
export const CLIP_REFRESH_MS = 15000;
export const CLIP_REFRESH_MAX_MS = 30 * 60 * 1000;

/** True when some clip card in the conversation is still being made. */
export function hasClipBeingMade(conversation) {
  return (conversation?.display || []).some((d) => (d.cards || []).some((c) => c.kind === 'clip' && c.status === 'working'));
}

/** The Stream id to play for a clip card (the captioned copy when there is one), or null. */
export function clipPlayUid(card) {
  const uid = card && card.status === 'ready' ? (card.playUid || card.uid) : null;
  return typeof uid === 'string' && /^[a-f0-9]{32}$/i.test(uid) ? uid : null;
}

export const STARTERS = [
  'How did my last webinar go?',
  'Find the best clip moments from my latest recording',
  'Make 3 vertical clips from my last webinar, framed on me',
  'Make square clips for LinkedIn that show my slides too',
  'Draft the promotion for my next session',
  'What did I schedule to post this week?',
  'Is my latest clip ready for Reels?',
];

/** Whether the page should keep asking for updates. */
export function isRunning(conversation) {
  return conversation?.status === 'running' || conversation?.status === 'stopping';
}

export function isWaiting(conversation) {
  return conversation?.status === 'waiting';
}

/** Speech wants words, not formatting: drop markdown marks and links' addresses. */
export function speakableText(text) {
  return String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/\[([^\]]+)\]\((?:[^)]+)\)/g, '$1')
    .replace(/https?:\/\/\S+/g, 'the link')
    .replace(/[*_#>`|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function readAloudSupported() {
  return typeof window !== 'undefined'
    && !!window.speechSynthesis
    && typeof window.SpeechSynthesisUtterance === 'function';
}

export function readAloudPref() {
  try { return window.localStorage.getItem(READ_ALOUD_KEY) === '1'; } catch (_) { return false; }
}

export function saveReadAloudPref(on) {
  try { window.localStorage.setItem(READ_ALOUD_KEY, on ? '1' : '0'); } catch (_) { /* per-device convenience only */ }
}

/** Read a reply out with the browser's own voice. Never throws. */
// `onEnd` runs when reading stops by itself, is stopped, or fails, so the
// page can take its Stop button away.
export function speak(text, onEnd) {
  if (!readAloudSupported()) return false;
  const words = speakableText(text);
  if (!words) return false;
  try {
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(words.slice(0, 3000));
    if (typeof navigator !== 'undefined' && navigator.language) u.lang = navigator.language;
    if (typeof onEnd === 'function') {
      let done = false;
      const finish = () => { if (!done) { done = true; onEnd(); } };
      u.onend = finish;
      u.onerror = finish;
    }
    window.speechSynthesis.speak(u);
    return true;
  } catch (_) {
    return false;
  }
}

// The natural voice playing now (an <audio> of the server's MP3), if any.
// `token` changes on every stop, so audio that arrives after Stop is dropped.
const natural = { token: 0, audio: null, url: null };

function dropAudio() {
  try { if (natural.audio) natural.audio.pause(); } catch (_) { /* already stopped */ }
  try { if (natural.url && typeof URL !== 'undefined' && URL.revokeObjectURL) URL.revokeObjectURL(natural.url); } catch (_) { /* fine */ }
  natural.audio = null;
  natural.url = null;
}

export function stopSpeaking() {
  natural.token += 1;
  dropAudio();
  try { if (readAloudSupported()) window.speechSynthesis.cancel(); } catch (_) { /* nothing to stop */ }
}

/**
 * Read a reply in the natural voice: `fetchAudio(text)` resolves to an MP3
 * Blob (POST /ask-canira/speak). If that fails, or the browser won't play it,
 * the browser's own voice reads it instead. `onEnd` runs once, whichever way
 * it ends. Without `fetchAudio` this is the browser voice alone.
 */
export function speakNatural(text, onEnd, fetchAudio) {
  if (typeof fetchAudio !== 'function') return speak(text, onEnd);
  const words = speakableText(text);
  if (!words) return false;
  stopSpeaking();
  const token = natural.token;
  let done = false;
  const finish = () => { if (!done) { done = true; if (typeof onEnd === 'function') onEnd(); } };
  const fallback = () => {
    if (token !== natural.token) return;
    dropAudio();
    if (!speak(text, finish)) finish();
  };
  Promise.resolve()
    .then(() => fetchAudio(words))
    .then((blob) => {
      if (token !== natural.token) return;
      if (!blob || typeof Audio === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) { fallback(); return; }
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      natural.audio = audio;
      natural.url = url;
      audio.onended = () => { if (token === natural.token) dropAudio(); finish(); };
      audio.onerror = fallback;
      const played = audio.play();
      if (played && typeof played.catch === 'function') played.catch(fallback);
    })
    .catch(fallback);
  return true;
}

/**
 * The reply that just finished, if the latest update finished one: the last
 * entry was pending before and isn't now. Used to read it aloud once.
 */
export function newlyFinishedReply(before, after) {
  const prev = before?.display?.[before.display.length - 1];
  const next = after?.display?.[after.display.length - 1];
  if (!prev || !next || next.role !== 'assistant') return null;
  if (prev.pending && !next.pending && next.text) return next;
  return null;
}

/**
 * One line of the "what Canira did" list. Repeats show a count, and a step
 * that failed and then worked reads as tried again rather than as a failure.
 */
export function stepLine(step) {
  const count = step.count > 1 ? ` ×${step.count}` : '';
  if (step.ok) return { mark: '✓', text: `${step.title}${count}`, tone: 'ok' };
  if (step.retried) return { mark: '↻', text: `${step.title}${count} (tried again)`, tone: 'retried' };
  return { mark: '!', text: `${step.title}${count} (didn’t work)`, tone: 'bad' };
}

/** "Waiting for Get clip suggestions… 20s" while a check tool waits on its job. */
export function waitingLine(waiting) {
  if (!waiting || !waiting.title) return null;
  const total = Math.round(Number(waiting.seconds) || 0);
  if (total <= 0) return `Waiting for ${waiting.title}…`;
  const time = total < 60 ? `${total}s` : `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
  return `Waiting for ${waiting.title}… ${time}`;
}
